import * as PIXI from "pixi.js";
import {
  collectSpineAssetsFromPreset,
  resolveSpineAsset,
  useSkinStore,
} from "../store/skinStore";
import { useSpritesheetStore } from "../store/spritesheetStore";
import {
  buildAssetUrl,
  ensureAssetsInit,
  getAssetResolution,
} from "./assetsInit";

const GAME_TYPE = (import.meta.env.VITE_GAME_TYPE || "twins") as
  | "pioneer"
  | "twins";
const STORAGE_KEY = "game_skin_selection";

function getSelectedSkinId(): number {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved);
      return data[GAME_TYPE] ?? 0;
    }
  } catch {
    // ignore
  }
  return 0;
}

function getSkinFolder(skinId: number): string {
  const skinFolders: Record<number, string> = {
    0: "default",
    1: "winter",
  };
  return skinFolders[skinId] || "default";
}

async function fetchAndAdjustManifest(prefix: string) {
  const manifestUrl = buildAssetUrl(`${prefix}/manifest.json`);
  try {
    const res = await fetch(manifestUrl);

    let manifest: any;
    try {
      manifest = await res.json();
    } catch (parseErr) {
      console.warn(`Failed to parse JSON manifest`);
      return null;
    }

    if (manifest?.bundles) {
      manifest.bundles.forEach((bundle: any) => {
        // Filter out individual Avatar_X files (png/webp) - they are included in avatars spritesheet
        bundle.assets = bundle.assets.filter((asset: any) => {
          const aliases = Array.isArray(asset.alias)
            ? asset.alias
            : [asset.alias];
          const isIndividualAvatar = aliases?.some(
            (a: any) =>
              typeof a === "string" && /^Avatar_\d+\.(png|webp)$/i.test(a)
          );
          return !isIndividualAvatar;
        });

        bundle.assets.forEach((asset: any) => {
          const aliases = Array.isArray(asset.alias)
            ? asset.alias
            : [asset.alias];
          const aliasHit = aliases?.some(
            (a: any) =>
              typeof a === "string" && a.toLowerCase().includes("avatars")
          );
          const srcArr = Array.isArray(asset.src) ? asset.src : [asset.src];
          const srcHit = srcArr?.some(
            (s: any) => typeof s === "string" && s.includes("avatars")
          );
          const isAvatars = !!(aliasHit || srcHit);

          if (isAvatars) {
            // Спрайтшит avatars: оставляем все варианты (1x/0.5x/0.25x/0.125x),
            // но фильтруем только .png.json — webp-варианты в текущей сборке
            // могут не подхватываться корректно.
            const srcs = Array.isArray(asset.src) ? asset.src : [asset.src];
            asset.src = (srcs as string[])
              .filter(
                (s) =>
                  typeof s === "string" && s.toLowerCase().endsWith(".png.json")
              )
              .map((s) => buildAssetUrl(`${prefix}/${s}`));
          } else {
            if (Array.isArray(asset.src)) {
              asset.src = (asset.src as string[]).map((src: string) =>
                buildAssetUrl(`${prefix}/${src}`)
              );
            } else {
              asset.src = buildAssetUrl(`${prefix}/${asset.src}`);
            }
          }
        });
      });
    }

    return manifest;
  } catch (e) {
    console.warn(`startupAssets: cannot load manifest`, {
      prefix,
      manifestUrl,
      error: e,
    });
    return null;
  }
}

function mergeIntoBundles(manifests: any[], bundleName: string): any {
  const assets: any[] = [];

  for (const m of manifests) {
    if (!m?.bundles) continue;
    for (const b of m.bundles) {
      if (Array.isArray(b.assets)) assets.push(...b.assets);
    }
  }

  return { bundles: [{ name: bundleName, assets }] };
}

const logPhase = (phase: string, message: string, data?: any) => {
  const timestamp = new Date().toISOString().split("T")[1].slice(0, -1);
  console.log(
    `%c[${timestamp}] 📦 ${phase}%c ${message}`,
    "color: #4CAF50; font-weight: bold",
    "color: inherit",
    data ?? ""
  );
};

/**
 * Phase 1: Load only the loader spine animation
 * This should be called first, before showing the loader
 */
export async function initLoaderAssets() {
  const startTime = performance.now();
  logPhase("Phase 1", "Starting loader assets...");

  const spinePrefix = "/spineOptimized";
  const spineM = await fetchAndAdjustManifest(spinePrefix);

  if (!spineM?.bundles) {
    console.warn("initLoaderAssets: no spine manifest found");
    return null;
  }

  // Filter only loading assets
  const loaderAssets: any[] = [];
  const gamePrefix = GAME_TYPE === "pioneer" ? "pioneer-game" : "twins-game";

  for (const bundle of spineM.bundles) {
    for (const asset of bundle.assets) {
      const src = Array.isArray(asset.src) ? asset.src[0] : asset.src;
      const srcStr = typeof src === "string" ? src : "";

      if (srcStr.includes("loading") && srcStr.includes(gamePrefix)) {
        loaderAssets.push(asset);
      }
    }
  }

  if (loaderAssets.length === 0) {
    console.warn("initLoaderAssets: no loading assets found");
    return null;
  }

  logPhase(
    "Phase 1",
    `Found ${loaderAssets.length} loader assets (resolution: ${getAssetResolution()})`
  );

  // Инициализируем PIXI.Assets с настройкой mip-уровня (texturePreference)
  // — идемпотентно — и регистрируем bundle лоадера.
  await ensureAssetsInit();
  PIXI.Assets.addBundle("loader", loaderAssets);

  await PIXI.Assets.loadBundle("loader");

  const duration = (performance.now() - startTime).toFixed(0);
  logPhase("Phase 1", `✅ Loader assets loaded in ${duration}ms`);

  return { bundles: [{ name: "loader", assets: loaderAssets }] };
}

/**
 * Phase 2: Load main non-spine assets (spritesheets / UI images from asset_main).
 * Spine игры грузятся в хуках (resolveSpineAsset), без дубля с манифестом spine.
 * This is called after loader is visible
 */
export async function initStartupAssets(
  onProgress?: (progress: number) => void
) {
  const startTime = performance.now();
  const selectedSkinId = getSelectedSkinId();
  const selectedSkinFolder = getSkinFolder(selectedSkinId);

  logPhase(
    "Phase 2",
    `Starting main assets (skin: ${selectedSkinFolder}, id: ${selectedSkinId})...`
  );

  await ensureAssetsInit();

  const mainPrefix = "/asset_mainOptimized";

  const mainM = await fetchAndAdjustManifest(mainPrefix);

  // Spine (кроме лоадера из phase 1) не кладём в startup-bundle: каждый скелет
  // уже грузится в хуках через resolveSpineAsset + applyAssetResolution.
  // Дублирование с манифестом давало двойной fetch и смешение 1x / @0.5x URL.
  const merged = mergeIntoBundles([mainM].filter(Boolean), "startup");

  // Add bundle to existing manifest (don't re-init)
  const startupAssets = merged.bundles[0]?.assets || [];
  const mainAssetsCount = mainM?.bundles?.[0]?.assets?.length || 0;

  logPhase(
    "Phase 2",
    `Loading ${mainAssetsCount} main images (spines — по хукам после монтирования)...`
  );

  if (startupAssets.length > 0) {
    PIXI.Assets.addBundle("startup", startupAssets);
    await PIXI.Assets.loadBundle("startup", onProgress);
  } else {
    onProgress?.(1);
  }

  // Verify avatars spritesheet is loaded and notify store
  const avatarsLoaded = PIXI.Assets.cache?.has?.("avatars");
  logPhase("Phase 2", `Avatars spritesheet loaded: ${avatarsLoaded}`);
  if (avatarsLoaded) {
    useSpritesheetStore.getState().setAvatarsReady(true);
  }

  const duration = (performance.now() - startTime).toFixed(0);
  logPhase("Phase 2", `✅ Main assets loaded in ${duration}ms`);

  return merged;
}

/**
 * Phase 3: Load secondary assets (base images + other skins)
 * This runs in background after game is playable
 */
export async function initBaseAssets() {
  const startTime = performance.now();
  logPhase("Phase 3", "Starting base assets (background)...");

  const basePrefix = "/asset_baseOptimized";
  const baseM = await fetchAndAdjustManifest(basePrefix);

  const baseAssets: any[] = [];
  if (baseM?.bundles?.length) {
    for (const b of baseM.bundles) {
      if (Array.isArray(b.assets)) baseAssets.push(...b.assets);
    }
  }

  logPhase("Phase 3", `Loading ${baseAssets.length} base assets...`);

  if (baseAssets.length > 0) {
    PIXI.Assets.addBundle("base", baseAssets);
    await PIXI.Assets.loadBundle("base");
  }

  const duration = (performance.now() - startTime).toFixed(0);
  logPhase("Phase 3", `✅ Base assets loaded in ${duration}ms`);

  return { bundles: [{ name: "base", assets: baseAssets }] };
}

/**
 * Phase 4: в фоне подгружаем spine **других** скинов (не выбранного).
 * URL совпадают с `resolveSpineAsset` из хуков — при смене скина сеть не дергается повторно.
 */
export function initOtherSkins(): void {
  const startTime = performance.now();
  const selectedId = getSelectedSkinId();
  const selectedFolder = getSkinFolder(selectedId);

  const presets = useSkinStore
    .getState()
    .getAvailableSkins(GAME_TYPE as GameType)
    .filter((p) => p.id !== selectedId);

  const urlSet = new Set<string>();
  for (const preset of presets) {
    for (const spine of collectSpineAssetsFromPreset(preset)) {
      const r = resolveSpineAsset(spine);
      urlSet.add(r.skeleton);
      urlSet.add(r.atlas);
    }
  }

  const urls = [...urlSet];
  if (urls.length === 0) {
    logPhase(
      "Phase 4",
      `No other-skin spines to preload (active: ${selectedFolder}).`
    );
    return;
  }

  logPhase(
    "Phase 4",
    `Background preload: ${urls.length} spine URLs for skins except «${selectedFolder}»...`
  );

  PIXI.Assets.load(urls)
    .then(() => {
      const duration = (performance.now() - startTime).toFixed(0);
      logPhase("Phase 4", `✅ Other skins spines warmed in ${duration}ms`);
    })
    .catch((e) => {
      console.warn("initOtherSkins: preload failed", e);
    });
}
